import { Box, Tooltip } from '@mantine/core';
import { ActionButton, ActionButtonProps } from 'components/ActionButton';
import { useMaintenanceMode } from 'hooks/queries/useMaintenanceMode';
import { useMoonChain } from 'hooks/useMoonChainConfig';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useAccount } from 'wagmi';

export interface ActionCallButtonProps extends ActionButtonProps {
  checkMoonChain?: boolean;
}

export const ActionCallButton = (props: ActionCallButtonProps) => {
  const shouldCheckMoonChain = props.checkMoonChain ?? false;

  const { isConnected, connector, chainId: accChainId } = useAccount();
  const moonChain = useMoonChain();
  const { t } = useTranslation();
  const isMaintenance = useMaintenanceMode();
  const { name, id } = useMoonChain();
  const isWrongChain = id !== accChainId;

  const label = useMemo(() => {
    if (isMaintenance) {
      return t('maintenanceModeDefaultMessage', { chain: name });
    }

    if (!shouldCheckMoonChain) {
      return;
    }

    if (!isConnected) {
      return t('notConnected', { ns: 'common' });
    }

    if (isWrongChain) {
      return t('wrongChainTooltip', {
        ns: 'common',
        network: moonChain.name,
        wallet: connector?.name,
      });
    }
  }, [
    connector?.name,
    isConnected,
    isMaintenance,
    isWrongChain,
    moonChain.name,
    name,
    shouldCheckMoonChain,
    t,
  ]);

  const disabled = useMemo(() => {
    if (!shouldCheckMoonChain) {
      return isMaintenance;
    }

    return isMaintenance || !isConnected || isWrongChain;
  }, [isConnected, isMaintenance, isWrongChain, shouldCheckMoonChain]);

  return (
    <Tooltip disabled={!disabled} label={label}>
      <Box>
        <ActionButton
          {...props}
          disabled={disabled || props.disabled}
          variant="outline"
        />
      </Box>
    </Tooltip>
  );
};
